import { ChevronDown, ChevronRight, Database, Layers, Table2 } from 'lucide-react';
import { useState } from 'react';
import { formatNumber } from '../../lib/data-catalog-format.js';
import type {
  CatalogDataset,
  CatalogTable,
  FirestoreCollectionMeta,
  SelectedNode,
} from '../../types/data-catalog.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business データカタログのツリーペイン。BQ データセット→テーブルと Firestore コレクションを階層表示する。
 * @graph-connects none
 */
export function TreePane({
  datasets,
  collections,
  selectedNode,
  onSelectNode,
}: {
  datasets: CatalogDataset[];
  collections: FirestoreCollectionMeta[];
  selectedNode: SelectedNode;
  onSelectNode: (node: SelectedNode) => void;
}) {
  const martDatasets = datasets.filter((d) => d.layer === 'mart');
  const rawDatasets = datasets.filter((d) => d.layer === 'raw');

  return (
    <div className="space-y-1 text-sm">
      <TreeSection
        label="BigQuery"
        icon={<Database className="h-4 w-4 text-[var(--color-accent-primary)]" />}
      >
        {martDatasets.map((ds) => (
          <DatasetNode
            key={ds.datasetId}
            dataset={ds}
            selectedNode={selectedNode}
            onSelectNode={onSelectNode}
          />
        ))}
        {rawDatasets.length > 0 && (
          <CollapsibleGroup label={`raw層 (${String(rawDatasets.length)})`} defaultOpen={false}>
            {rawDatasets.map((ds) => (
              <DatasetNode
                key={ds.datasetId}
                dataset={ds}
                selectedNode={selectedNode}
                onSelectNode={onSelectNode}
              />
            ))}
          </CollapsibleGroup>
        )}
      </TreeSection>

      {collections.length > 0 && (
        <TreeSection
          label="Firestore"
          icon={<Layers className="h-4 w-4 text-[var(--color-warning)]" />}
        >
          <CollapsibleGroup label={`ac_mens_hub/_/ (${String(collections.length)})`} defaultOpen>
            {collections.map((coll) => (
              <CollectionNode
                key={coll.shortName}
                collection={coll}
                isSelected={
                  selectedNode?.type === 'fs-collection' &&
                  selectedNode.shortName === coll.shortName
                }
                onSelect={() => {
                  onSelectNode({ type: 'fs-collection', shortName: coll.shortName });
                }}
              />
            ))}
          </CollapsibleGroup>
        </TreeSection>
      )}
    </div>
  );
}

/** @graph-connects none */
function TreeSection({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 py-1.5 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="ml-2 border-l border-[var(--color-border)] pl-2">{children}</div>
    </div>
  );
}

/** @graph-connects none */
function CollapsibleGroup({
  label,
  defaultOpen,
  children,
}: {
  label: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]"
        onClick={() => {
          setOpen(!open);
        }}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        {label}
      </button>
      {open && <div className="ml-3">{children}</div>}
    </div>
  );
}

/** @graph-connects none */
function DatasetNode({
  dataset,
  selectedNode,
  onSelectNode,
}: {
  dataset: CatalogDataset;
  selectedNode: SelectedNode;
  onSelectNode: (node: SelectedNode) => void;
}) {
  const [expanded, setExpanded] = useState(dataset.layer === 'mart');
  const isDatasetSelected =
    selectedNode?.type === 'bq-dataset' && selectedNode.datasetId === dataset.datasetId;

  return (
    <div>
      <button
        type="button"
        className={`flex w-full items-center gap-1.5 rounded px-1 py-1 text-xs transition-colors ${
          isDatasetSelected
            ? 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
            : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-base)]'
        }`}
        onClick={() => {
          if (dataset.tables.length > 0) {
            setExpanded(!expanded);
          }
          onSelectNode({ type: 'bq-dataset', datasetId: dataset.datasetId });
        }}
      >
        {dataset.tables.length > 0 ? (
          expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )
        ) : (
          <span className="h-3 w-3 shrink-0" />
        )}
        <Database className="h-3 w-3 shrink-0 text-[var(--color-accent-primary)]" />
        <span className="truncate font-mono">{dataset.datasetId}</span>
        <span className="ml-auto shrink-0 text-[var(--color-text-muted)]">
          {dataset.tables.length}
        </span>
      </button>
      {expanded && (
        <div className="ml-4">
          {dataset.tables.map((table) => (
            <TableNode
              key={table.tableId}
              table={table}
              datasetId={dataset.datasetId}
              isSelected={
                selectedNode?.type === 'bq-table' &&
                selectedNode.datasetId === dataset.datasetId &&
                selectedNode.tableId === table.tableId
              }
              onSelect={() => {
                onSelectNode({
                  type: 'bq-table',
                  datasetId: dataset.datasetId,
                  tableId: table.tableId,
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function TableNode({
  table,
  datasetId: _datasetId,
  isSelected,
  onSelect,
}: {
  table: CatalogTable;
  datasetId: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-xs transition-colors ${
        isSelected
          ? 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
          : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-base)]'
      }`}
      onClick={onSelect}
    >
      <Table2 className="h-3 w-3 shrink-0 text-[var(--color-text-muted)]" />
      <span className="truncate font-mono">{table.tableId}</span>
      <span
        className={`ml-1 shrink-0 rounded-full px-1.5 py-0 text-[9px] font-medium ${
          table.tableType === 'VIEW'
            ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
            : 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
        }`}
      >
        {table.tableType}
      </span>
      <span className="ml-auto shrink-0 text-[var(--color-text-muted)]">
        {formatNumber(table.rowCount)}行
      </span>
    </button>
  );
}

/** @graph-connects none */
function CollectionNode({
  collection,
  isSelected,
  onSelect,
}: {
  collection: FirestoreCollectionMeta;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-xs transition-colors ${
        isSelected
          ? 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
          : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-base)]'
      }`}
      onClick={onSelect}
    >
      <Layers className="h-3 w-3 shrink-0 text-[var(--color-warning)]" />
      <span className="truncate font-mono">{collection.shortName}</span>
      <span className="ml-auto shrink-0 text-[var(--color-text-muted)]">
        {formatNumber(collection.docCount)} docs
      </span>
    </button>
  );
}
